import { a as VSlideYTransition, n as nullifyTransforms, b as animate, s as standardEasing, c as VExpandXTransition } from "./index-dbde2d54.mjs";
import { P as includes, p as propsFactory, m as makeComponentProps, y as makeTransitionProps, a as genericComponent, u as useRender, M as MaybeTransition, E as EventProp, e as makeThemeProps, F as VIcon, B as getCurrentInstanceName, S as SUPPORTS_INTERSECTION, n as makeRoundedProps, b as makeTagProps, f as provideTheme, c as useTextColor, q as useBackgroundColor, v as useRounded, Q as clamp, h as convertToUnit, I as IconValue, H as getUid, V as VDefaultsProvider, R as isOn, T as pick, G as wrapInArray, g as getCurrentInstance, k as makeDensityProps, U as only, l as makeDimensionProps, r as useDensity, s as useDimension, z as Intersect, W as filterInputAttrs, X as callEvent } from "./_density-8094aebe.mjs";
import { computed, inject, ref, shallowRef, onBeforeUnmount, watch, Transition, toRef, unref, onBeforeMount, onMounted, nextTick, cloneVNode } from "vue";
import { u as useProxiedModel, a as useToggleScope } from "./_proxiedModel-031e293c.mjs";
const block = ["top", "bottom"];
const inline = ["start", "end", "left", "right"];
function parseAnchor(anchor, isRtl) {
  let [side, align] = anchor.split(" ");
  if (!align) {
    align = includes(block, side) ? "start" : includes(inline, side) ? "top" : "center";
  }
  return {
    side: toPhysical(side, isRtl),
    align: toPhysical(align, isRtl)
  };
}
function toPhysical(str, isRtl) {
  if (str === "start")
    return isRtl ? "right" : "left";
  if (str === "end")
    return isRtl ? "left" : "right";
  return str;
}
function flipSide(anchor) {
  return {
    side: {
      center: "center",
      top: "bottom",
      bottom: "top",
      left: "right",
      right: "left"
    }[anchor.side],
    align: anchor.align
  };
}
function flipAlign(anchor) {
  return {
    side: anchor.side,
    align: {
      center: "center",
      top: "bottom",
      bottom: "top",
      left: "right",
      right: "left"
    }[anchor.align]
  };
}
function flipCorner(anchor) {
  return {
    side: anchor.align,
    align: anchor.side
  };
}
function getAxis(anchor) {
  return includes(block, anchor.side) ? "y" : "x";
}
const _VTextField = "";
const _VCounter = "";
const makeVCounterProps = propsFactory({
  active: Boolean,
  disabled: Boolean,
  max: [Number, String],
  value: {
    type: [Number, String],
    default: 0
  },
  ...makeComponentProps(),
  ...makeTransitionProps({
    transition: { component: VSlideYTransition }
  })
}, "VCounter");
const VCounter = genericComponent()({
  name: "VCounter",
  functional: true,
  props: makeVCounterProps(),
  setup(props, { slots }) {
    const counter = computed(() => {
      return props.max ? `${props.value} / ${props.max}` : String(props.value);
    });
    useRender(() => /* @__PURE__ */ React.createElement(MaybeTransition, { transition: props.transition }, /* @__PURE__ */ React.createElement(
      "div",
      {
        "v-show": props.active,
        class: [
          "v-counter",
          {
            "text-error": props.max && !props.disabled && parseFloat(props.value) > parseFloat(props.max)
          },
          props.class
        ],
        style: props.style
      },
      slots.default ? slots.default({
        counter: counter.value,
        max: props.max,
        value: props.value
      }) : counter.value
    )));
    return {};
  }
});
const _VField = "";
const _VLabel = "";
const makeVLabelProps = propsFactory({
  text: String,
  onClick: EventProp(),
  ...makeComponentProps(),
  ...makeThemeProps()
}, "VLabel");
const VLabel = genericComponent()({
  name: "VLabel",
  props: makeVLabelProps(),
  setup(props, { slots }) {
    useRender(() => {
      var _a;
      return /* @__PURE__ */ React.createElement(
        "label",
        {
          class: [
            "v-label",
            {
              "v-label--clickable": !!props.onClick
            },
            props.class
          ],
          style: props.style,
          onClick: props.onClick
        },
        props.text,
        (_a = slots.default) == null ? void 0 : _a.call(slots)
      );
    });
    return {};
  }
});
const makeVFieldLabelProps = propsFactory({
  floating: Boolean,
  ...makeComponentProps()
}, "VFieldLabel");
const VFieldLabel = genericComponent()({
  name: "VFieldLabel",
  props: makeVFieldLabelProps(),
  setup(props, { slots }) {
    useRender(() => /* @__PURE__ */ React.createElement(
      VLabel,
      {
        class: [
          "v-field-label",
          { "v-field-label--floating": props.floating },
          props.class
        ],
        style: props.style,
        "aria-hidden": props.floating || void 0,
        "v-slots": slots
      }
    ));
    return {};
  }
});
const LocaleSymbol = Symbol.for("vuetify:locale");
function useLocale() {
  const locale = inject(LocaleSymbol);
  if (!locale)
    throw new Error("[Vuetify] Could not find injected locale instance");
  return locale;
}
function useRtl() {
  const locale = inject(LocaleSymbol);
  if (!locale)
    throw new Error("[Vuetify] Could not find injected rtl instance");
  return { isRtl: locale.isRtl, rtlClasses: locale.rtlClasses };
}
function useInputIcon(props) {
  const { t } = useLocale();
  function InputIcon({ name }) {
    const localeKey = {
      prepend: "prependAction",
      prependInner: "prependAction",
      append: "appendAction",
      appendInner: "appendAction",
      clear: "clear"
    }[name];
    const listener = props[`onClick:${name}`];
    const label = listener && localeKey ? t(`$vuetify.input.${localeKey}`, props.label ?? "") : void 0;
    return /* @__PURE__ */ React.createElement(
      VIcon,
      {
        icon: props[`${name}Icon`],
        "aria-label": label,
        onClick: listener
      }
    );
  }
  return { InputIcon };
}
const makeFocusProps = propsFactory({
  focused: Boolean,
  "onUpdate:focused": EventProp()
}, "focus");
function useFocus(props, name = getCurrentInstanceName()) {
  const isFocused = useProxiedModel(props, "focused");
  const focusClasses = computed(() => {
    return {
      [`${name}--focused`]: isFocused.value
    };
  });
  function focus() {
    isFocused.value = true;
  }
  function blur() {
    isFocused.value = false;
  }
  return { focusClasses, isFocused, focus, blur };
}
const _VProgressLinear = "";
function useIntersectionObserver(callback, options) {
  const intersectionRef = ref();
  const isIntersecting = shallowRef(false);
  if (SUPPORTS_INTERSECTION) {
    const observer = new IntersectionObserver((entries) => {
      callback == null ? void 0 : callback(entries, observer);
      isIntersecting.value = !!entries.find((entry) => entry.isIntersecting);
    }, options);
    onBeforeUnmount(() => {
      observer.disconnect();
    });
    watch(intersectionRef, (newValue, oldValue) => {
      if (oldValue) {
        observer.unobserve(oldValue);
        isIntersecting.value = false;
      }
      if (newValue)
        observer.observe(newValue);
    }, {
      flush: "post"
    });
  }
  return { intersectionRef, isIntersecting };
}
const oppositeMap = {
  center: "center",
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left"
};
const makeLocationProps = propsFactory({
  location: String
}, "location");
function useLocation(props, opposite = false, offset) {
  const { isRtl } = useRtl();
  const locationStyles = computed(() => {
    if (!props.location)
      return {};
    const { side, align } = parseAnchor(
      props.location.split(" ").length > 1 ? props.location : `${props.location} center`,
      isRtl.value
    );
    function getOffset(side2) {
      return offset ? offset(side2) : 0;
    }
    const styles = {};
    if (side !== "center") {
      if (opposite)
        styles[oppositeMap[side]] = `calc(100% - ${getOffset(side)}px)`;
      else
        styles[side] = 0;
    }
    if (align !== "center") {
      if (opposite)
        styles[oppositeMap[align]] = `calc(100% - ${getOffset(align)}px)`;
      else
        styles[align] = 0;
    } else {
      if (side === "center")
        styles.top = styles.left = "50%";
      else {
        styles[{
          top: "left",
          bottom: "left",
          left: "top",
          right: "top"
        }[side]] = "50%";
      }
      styles.transform = {
        top: "translateX(-50%)",
        bottom: "translateX(-50%)",
        left: "translateY(-50%)",
        right: "translateY(-50%)",
        center: "translate(-50%, -50%)"
      }[side];
    }
    return styles;
  });
  return { locationStyles };
}
const makeVProgressLinearProps = propsFactory({
  absolute: Boolean,
  active: {
    type: Boolean,
    default: true
  },
  bgColor: String,
  bgOpacity: [Number, String],
  bufferValue: {
    type: [Number, String],
    default: 0
  },
  bufferColor: String,
  bufferOpacity: [Number, String],
  clickable: Boolean,
  color: String,
  height: {
    type: [Number, String],
    default: 4
  },
  indeterminate: Boolean,
  max: {
    type: [Number, String],
    default: 100
  },
  modelValue: {
    type: [Number, String],
    default: 0
  },
  opacity: [Number, String],
  reverse: Boolean,
  stream: Boolean,
  striped: Boolean,
  roundedBar: Boolean,
  ...makeComponentProps(),
  ...makeLocationProps({ location: "top" }),
  ...makeRoundedProps(),
  ...makeTagProps(),
  ...makeThemeProps()
}, "VProgressLinear");
const VProgressLinear = genericComponent()({
  name: "VProgressLinear",
  props: makeVProgressLinearProps(),
  emits: {
    "update:modelValue": (value) => true
  },
  setup(props, { slots }) {
    const progress = useProxiedModel(props, "modelValue");
    const { isRtl, rtlClasses } = useRtl();
    const { themeClasses } = provideTheme(props);
    const { locationStyles } = useLocation(props);
    const { textColorClasses, textColorStyles } = useTextColor(props, "color");
    const {
      backgroundColorClasses,
      backgroundColorStyles
    } = useBackgroundColor(computed(() => props.bgColor || props.color));
    const {
      backgroundColorClasses: bufferColorClasses,
      backgroundColorStyles: bufferColorStyles
    } = useBackgroundColor(computed(() => props.bufferColor || props.bgColor || props.color));
    const {
      backgroundColorClasses: barColorClasses,
      backgroundColorStyles: barColorStyles
    } = useBackgroundColor(props, "color");
    const { roundedClasses } = useRounded(props);
    const { intersectionRef, isIntersecting } = useIntersectionObserver();
    const max = computed(() => parseFloat(props.max));
    const height = computed(() => parseFloat(props.height));
    const normalizedBuffer = computed(() => clamp(parseFloat(props.bufferValue) / max.value * 100, 0, 100));
    const normalizedValue = computed(() => clamp(parseFloat(progress.value) / max.value * 100, 0, 100));
    const isReversed = computed(() => isRtl.value !== props.reverse);
    const transition = computed(() => props.indeterminate ? "fade-transition" : "slide-x-transition");
    function handleClick(e) {
      if (!intersectionRef.value)
        return;
      const { left, right, width } = intersectionRef.value.getBoundingClientRect();
      const value = isReversed.value ? width - e.clientX + (right - width) : e.clientX - left;
      progress.value = Math.round(value / width * max.value);
    }
    useRender(() => /* @__PURE__ */ React.createElement(
      props.tag,
      {
        ref: intersectionRef,
        class: [
          "v-progress-linear",
          {
            "v-progress-linear--absolute": props.absolute,
            "v-progress-linear--active": props.active && isIntersecting.value,
            "v-progress-linear--reverse": isReversed.value,
            "v-progress-linear--rounded": props.rounded,
            "v-progress-linear--rounded-bar": props.roundedBar,
            "v-progress-linear--striped": props.striped
          },
          roundedClasses.value,
          themeClasses.value,
          rtlClasses.value,
          props.class
        ],
        style: [
          {
            bottom: props.location === "bottom" ? 0 : void 0,
            top: props.location === "top" ? 0 : void 0,
            height: props.active ? convertToUnit(height.value) : 0,
            "--v-progress-linear-height": convertToUnit(height.value),
            ...props.absolute ? locationStyles.value : {}
          },
          props.style
        ],
        role: "progressbar",
        "aria-hidden": props.active ? "false" : "true",
        "aria-valuemin": "0",
        "aria-valuemax": props.max,
        "aria-valuenow": props.indeterminate ? void 0 : normalizedValue.value,
        onClick: props.clickable && handleClick
      },
      props.stream && /* @__PURE__ */ React.createElement(
        "div",
        {
          key: "stream",
          class: [
            "v-progress-linear__stream",
            textColorClasses.value
          ],
          style: {
            ...textColorStyles.value,
            [isReversed.value ? "left" : "right"]: convertToUnit(-height.value),
            borderTop: `${convertToUnit(height.value / 2)} dotted`,
            opacity: parseFloat(props.bufferOpacity),
            top: `calc(50% - ${convertToUnit(height.value / 4)})`,
            width: convertToUnit(100 - normalizedBuffer.value, "%"),
            "--v-progress-linear-stream-to": convertToUnit(height.value * (isReversed.value ? 1 : -1))
          }
        }
      ),
      /* @__PURE__ */ React.createElement(
        "div",
        {
          class: [
            "v-progress-linear__background",
            backgroundColorClasses.value
          ],
          style: [
            backgroundColorStyles.value,
            {
              opacity: parseFloat(props.bgOpacity),
              width: props.stream ? 0 : void 0
            }
          ]
        }
      ),
      /* @__PURE__ */ React.createElement(
        "div",
        {
          class: [
            "v-progress-linear__buffer",
            bufferColorClasses.value
          ],
          style: [
            bufferColorStyles.value,
            {
              opacity: parseFloat(props.bufferOpacity),
              width: convertToUnit(normalizedBuffer.value, "%")
            }
          ]
        }
      ),
      /* @__PURE__ */ React.createElement(Transition, { name: transition.value }, !props.indeterminate ? /* @__PURE__ */ React.createElement(
        "div",
        {
          class: [
            "v-progress-linear__determinate",
            barColorClasses.value
          ],
          style: [
            barColorStyles.value,
            { width: convertToUnit(normalizedValue.value, "%") }
          ]
        }
      ) : /* @__PURE__ */ React.createElement("div", { class: "v-progress-linear__indeterminate" }, ["long", "short"].map((bar) => /* @__PURE__ */ React.createElement(
        "div",
        {
          key: bar,
          class: [
            "v-progress-linear__indeterminate",
            bar,
            barColorClasses.value
          ],
          style: barColorStyles.value
        }
      )))),
      slots.default && /* @__PURE__ */ React.createElement("div", { class: "v-progress-linear__content" }, slots.default({ value: normalizedValue.value, buffer: normalizedBuffer.value }))
    ));
    return {};
  }
});
const makeLoaderProps = propsFactory({
  loading: [Boolean, String]
}, "loader");
function useLoader(props, name = getCurrentInstanceName()) {
  const loaderClasses = computed(() => ({
    [`${name}--loading`]: props.loading
  }));
  return { loaderClasses };
}
function LoaderSlot(props, { slots }) {
  var _a;
  return /* @__PURE__ */ React.createElement("div", { class: `${props.name}__loader` }, ((_a = slots.default) == null ? void 0 : _a.call(slots, {
    color: props.color,
    isActive: props.active
  })) || /* @__PURE__ */ React.createElement(
    VProgressLinear,
    {
      absolute: props.absolute,
      active: props.active,
      color: props.color,
      height: "2",
      indeterminate: true
    }
  ));
}
const allowedVariants = ["underlined", "outlined", "filled", "solo", "solo-inverted", "solo-filled", "plain"];
const makeVFieldProps = propsFactory({
  appendInnerIcon: IconValue,
  bgColor: String,
  clearable: Boolean,
  clearIcon: {
    type: IconValue,
    default: "$clear"
  },
  active: Boolean,
  centerAffix: {
    type: Boolean,
    default: void 0
  },
  color: String,
  baseColor: String,
  dirty: Boolean,
  disabled: {
    type: Boolean,
    default: null
  },
  error: Boolean,
  flat: Boolean,
  label: String,
  persistentClear: Boolean,
  prependInnerIcon: IconValue,
  reverse: Boolean,
  singleLine: Boolean,
  variant: {
    type: String,
    default: "filled",
    validator: (v) => allowedVariants.includes(v)
  },
  "onClick:clear": EventProp(),
  "onClick:appendInner": EventProp(),
  "onClick:prependInner": EventProp(),
  ...makeComponentProps(),
  ...makeLoaderProps(),
  ...makeRoundedProps(),
  ...makeThemeProps()
}, "VField");
const VField = genericComponent()({
  name: "VField",
  inheritAttrs: false,
  props: {
    id: String,
    ...makeFocusProps(),
    ...makeVFieldProps()
  },
  emits: {
    "update:focused": (focused) => true,
    "update:modelValue": (value) => true
  },
  setup(props, { attrs, emit, slots }) {
    const { themeClasses } = provideTheme(props);
    const { loaderClasses } = useLoader(props);
    const { focusClasses, isFocused, focus, blur } = useFocus(props);
    const { InputIcon } = useInputIcon(props);
    const { roundedClasses } = useRounded(props);
    const { rtlClasses } = useRtl();
    const isActive = computed(() => props.dirty || props.active);
    const hasLabel = computed(() => !props.singleLine && !!(props.label || slots.label));
    const uid = getUid();
    const id = computed(() => props.id || `input-${uid}`);
    const messagesId = computed(() => `${id.value}-messages`);
    const labelRef = ref();
    const floatingLabelRef = ref();
    const controlRef = ref();
    const isPlainOrUnderlined = computed(() => ["plain", "underlined"].includes(props.variant));
    const { backgroundColorClasses, backgroundColorStyles } = useBackgroundColor(toRef(props, "bgColor"));
    const { textColorClasses, textColorStyles } = useTextColor(computed(() => {
      return props.error || props.disabled ? void 0 : isActive.value && isFocused.value ? props.color : props.baseColor;
    }));
    watch(isActive, (val) => {
      if (hasLabel.value) {
        const el = labelRef.value.$el;
        const targetEl = floatingLabelRef.value.$el;
        requestAnimationFrame(() => {
          const rect = nullifyTransforms(el);
          const targetRect = targetEl.getBoundingClientRect();
          const x = targetRect.x - rect.x;
          const y = targetRect.y - rect.y - (rect.height / 2 - targetRect.height / 2);
          const targetWidth = targetRect.width / 0.75;
          const width = Math.abs(targetWidth - rect.width) > 1 ? { maxWidth: convertToUnit(targetWidth) } : void 0;
          const style = getComputedStyle(el);
          const targetStyle = getComputedStyle(targetEl);
          const duration = parseFloat(style.transitionDuration) * 1e3 || 150;
          const scale = parseFloat(targetStyle.getPropertyValue("--v-field-label-scale"));
          const color = targetStyle.getPropertyValue("color");
          el.style.visibility = "visible";
          targetEl.style.visibility = "hidden";
          animate(el, {
            transform: `translate(${x}px, ${y}px) scale(${scale})`,
            color,
            ...width
          }, {
            duration,
            easing: standardEasing,
            direction: val ? "normal" : "reverse"
          }).finished.then(() => {
            el.style.removeProperty("visibility");
            targetEl.style.removeProperty("visibility");
          });
        });
      }
    }, { flush: "post" });
    const slotProps = computed(() => ({
      isActive,
      isFocused,
      controlRef,
      blur,
      focus
    }));
    function onClick(e) {
      if (e.target !== document.activeElement) {
        e.preventDefault();
      }
    }
    function onKeydownClear(e) {
      var _a;
      if (e.key !== "Enter" && e.key !== " ")
        return;
      e.preventDefault();
      e.stopPropagation();
      (_a = props["onClick:clear"]) == null ? void 0 : _a.call(props, new MouseEvent("click"));
    }
    useRender(() => {
      var _a, _b, _c;
      const isOutlined = props.variant === "outlined";
      const hasPrepend = !!(slots["prepend-inner"] || props.prependInnerIcon);
      const hasClear = !!(props.clearable || slots.clear);
      const hasAppend = !!(slots["append-inner"] || props.appendInnerIcon || hasClear);
      const label = () => slots.label ? slots.label({
        ...slotProps.value,
        label: props.label,
        props: { for: id.value }
      }) : props.label;
      return /* @__PURE__ */ React.createElement(
        "div",
        {
          class: [
            "v-field",
            {
              "v-field--active": isActive.value,
              "v-field--appended": hasAppend,
              "v-field--center-affix": props.centerAffix ?? !isPlainOrUnderlined.value,
              "v-field--disabled": props.disabled,
              "v-field--dirty": props.dirty,
              "v-field--error": props.error,
              "v-field--flat": props.flat,
              "v-field--has-background": !!props.bgColor,
              "v-field--persistent-clear": props.persistentClear,
              "v-field--prepended": hasPrepend,
              "v-field--reverse": props.reverse,
              "v-field--single-line": props.singleLine,
              "v-field--no-label": !label(),
              [`v-field--variant-${props.variant}`]: true
            },
            themeClasses.value,
            backgroundColorClasses.value,
            focusClasses.value,
            loaderClasses.value,
            roundedClasses.value,
            rtlClasses.value,
            props.class
          ],
          style: [
            backgroundColorStyles.value,
            props.style
          ],
          onClick,
          ...attrs
        },
        /* @__PURE__ */ React.createElement("div", { class: "v-field__overlay" }),
        /* @__PURE__ */ React.createElement(
          LoaderSlot,
          {
            name: "v-field",
            active: !!props.loading,
            color: props.error ? "error" : typeof props.loading === "string" ? props.loading : props.color,
            "v-slots": { default: slots.loader }
          }
        ),
        hasPrepend && /* @__PURE__ */ React.createElement("div", { key: "prepend", class: "v-field__prepend-inner" }, props.prependInnerIcon && /* @__PURE__ */ React.createElement(InputIcon, { key: "prepend-icon", name: "prependInner" }), (_a = slots["prepend-inner"]) == null ? void 0 : _a.call(slots, slotProps.value)),
        /* @__PURE__ */ React.createElement("div", { class: "v-field__field", "data-no-activator": "" }, ["filled", "solo", "solo-inverted", "solo-filled"].includes(props.variant) && hasLabel.value && /* @__PURE__ */ React.createElement(
          VFieldLabel,
          {
            key: "floating-label",
            ref: floatingLabelRef,
            class: [textColorClasses.value],
            floating: true,
            for: id.value,
            style: textColorStyles.value
          },
          label()
        ), /* @__PURE__ */ React.createElement(VFieldLabel, { ref: labelRef, for: id.value }, label()), (_b = slots.default) == null ? void 0 : _b.call(slots, {
          ...slotProps.value,
          props: {
            id: id.value,
            class: "v-field__input",
            "aria-describedby": messagesId.value
          },
          focus,
          blur
        })),
        hasClear && /* @__PURE__ */ React.createElement(VExpandXTransition, { key: "clear" }, /* @__PURE__ */ React.createElement(
          "div",
          {
            class: "v-field__clearable",
            "v-show": props.dirty,
            onMousedown: (e) => {
              e.preventDefault();
              e.stopPropagation();
            }
          },
          /* @__PURE__ */ React.createElement(
            VDefaultsProvider,
            {
              defaults: {
                VIcon: {
                  icon: props.clearIcon
                }
              }
            },
            slots.clear ? slots.clear({
              ...slotProps.value,
              props: {
                onKeydown: onKeydownClear,
                onFocus: focus,
                onBlur: blur,
                onClick: props["onClick:clear"]
              }
            }) : /* @__PURE__ */ React.createElement(
              InputIcon,
              {
                name: "clear",
                onKeydown: onKeydownClear,
                onFocus: focus,
                onBlur: blur
              }
            )
          )
        )),
        hasAppend && /* @__PURE__ */ React.createElement("div", { key: "append", class: "v-field__append-inner" }, (_c = slots["append-inner"]) == null ? void 0 : _c.call(slots, slotProps.value), props.appendInnerIcon && /* @__PURE__ */ React.createElement(InputIcon, { key: "append-icon", name: "appendInner" })),
        /* @__PURE__ */ React.createElement(
          "div",
          {
            class: [
              "v-field__outline",
              textColorClasses.value
            ],
            style: textColorStyles.value
          },
          isOutlined && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { class: "v-field__outline__start" }), hasLabel.value && /* @__PURE__ */ React.createElement("div", { class: "v-field__outline__notch" }, /* @__PURE__ */ React.createElement(VFieldLabel, { ref: floatingLabelRef, floating: true, for: id.value }, label())), /* @__PURE__ */ React.createElement("div", { class: "v-field__outline__end" })),
          isPlainOrUnderlined.value && hasLabel.value && /* @__PURE__ */ React.createElement(VFieldLabel, { ref: floatingLabelRef, floating: true, for: id.value }, label())
        )
      );
    });
    return {
      controlRef
    };
  }
});
function filterFieldProps(attrs) {
  const keys = Object.keys(VField.props).filter((k) => !isOn(k) && k !== "class" && k !== "style");
  return pick(attrs, keys);
}
const _VInput = "";
const _VMessages = "";
const makeVMessagesProps = propsFactory({
  active: Boolean,
  color: String,
  messages: {
    type: [Array, String],
    default: () => []
  },
  ...makeComponentProps(),
  ...makeTransitionProps({
    transition: {
      component: VSlideYTransition,
      leaveAbsolute: true,
      group: true
    }
  })
}, "VMessages");
const VMessages = genericComponent()({
  name: "VMessages",
  props: makeVMessagesProps(),
  setup(props, { slots }) {
    const messages = computed(() => wrapInArray(props.messages));
    const { textColorClasses, textColorStyles } = useTextColor(computed(() => props.color));
    useRender(() => /* @__PURE__ */ React.createElement(
      MaybeTransition,
      {
        transition: props.transition,
        tag: "div",
        class: [
          "v-messages",
          textColorClasses.value,
          props.class
        ],
        style: [
          textColorStyles.value,
          props.style
        ],
        role: "alert",
        "aria-live": "polite"
      },
      props.active && messages.value.map((message, i) => /* @__PURE__ */ React.createElement(
        "div",
        {
          class: "v-messages__message",
          key: `${i}-${messages.value}`
        },
        slots.message ? slots.message({ message }) : message
      ))
    ));
    return {};
  }
});
const FormKey = Symbol.for("vuetify:form");
function useForm() {
  return inject(FormKey, null);
}
const makeValidationProps = propsFactory({
  disabled: {
    type: Boolean,
    default: null
  },
  error: Boolean,
  errorMessages: {
    type: [Array, String],
    default: () => []
  },
  maxErrors: {
    type: [Number, String],
    default: 1
  },
  name: String,
  label: String,
  readonly: {
    type: Boolean,
    default: null
  },
  rules: {
    type: Array,
    default: () => []
  },
  modelValue: null,
  validateOn: String,
  validationValue: null,
  ...makeFocusProps()
}, "validation");
function useValidation(props, name = getCurrentInstanceName(), id = getUid()) {
  const model = useProxiedModel(props, "modelValue");
  const validationModel = computed(() => props.validationValue === void 0 ? model.value : props.validationValue);
  const form = useForm();
  const internalErrorMessages = ref([]);
  const isPristine = shallowRef(true);
  const isDirty = computed(() => !!(wrapInArray(model.value === "" ? null : model.value).length || wrapInArray(validationModel.value === "" ? null : validationModel.value).length));
  const isDisabled = computed(() => !!(props.disabled ?? (form == null ? void 0 : form.isDisabled.value)));
  const isReadonly = computed(() => !!(props.readonly ?? (form == null ? void 0 : form.isReadonly.value)));
  const errorMessages = computed(() => {
    var _a;
    return ((_a = props.errorMessages) == null ? void 0 : _a.length) ? wrapInArray(props.errorMessages).concat(internalErrorMessages.value).slice(0, Math.max(0, +props.maxErrors)) : internalErrorMessages.value;
  });
  const validateOn = computed(() => {
    let value = (props.validateOn ?? (form == null ? void 0 : form.validateOn.value)) || "input";
    if (value === "lazy")
      value = "input lazy";
    const set = new Set((value == null ? void 0 : value.split(" ")) ?? []);
    return {
      blur: set.has("blur") || set.has("input"),
      input: set.has("input"),
      submit: set.has("submit"),
      lazy: set.has("lazy")
    };
  });
  const isValid = computed(() => {
    var _a;
    if (props.error || ((_a = props.errorMessages) == null ? void 0 : _a.length))
      return false;
    if (!props.rules.length)
      return true;
    if (isPristine.value) {
      return internalErrorMessages.value.length || validateOn.value.lazy ? null : true;
    } else {
      return !internalErrorMessages.value.length;
    }
  });
  const isValidating = shallowRef(false);
  const validationClasses = computed(() => {
    return {
      [`${name}--error`]: isValid.value === false,
      [`${name}--dirty`]: isDirty.value,
      [`${name}--disabled`]: isDisabled.value,
      [`${name}--readonly`]: isReadonly.value
    };
  });
  const vm = getCurrentInstance("validation");
  const uid = computed(() => props.name ?? unref(id));
  onBeforeMount(() => {
    form == null ? void 0 : form.register({
      id: uid.value,
      vm,
      validate,
      reset,
      resetValidation
    });
  });
  onBeforeUnmount(() => {
    form == null ? void 0 : form.unregister(uid.value);
  });
  onMounted(async () => {
    if (!validateOn.value.lazy) {
      await validate(true);
    }
    form == null ? void 0 : form.update(uid.value, isValid.value, errorMessages.value);
  });
  useToggleScope(() => validateOn.value.input, () => {
    watch(validationModel, () => {
      if (validationModel.value != null) {
        validate();
      } else if (props.focused) {
        const unwatch = watch(() => props.focused, (val) => {
          if (!val)
            validate();
          unwatch();
        });
      }
    });
  });
  useToggleScope(() => validateOn.value.blur, () => {
    watch(() => props.focused, (val) => {
      if (!val)
        validate();
    });
  });
  watch([isValid, errorMessages], () => {
    form == null ? void 0 : form.update(uid.value, isValid.value, errorMessages.value);
  });
  async function reset() {
    model.value = null;
    await nextTick();
    await resetValidation();
  }
  async function resetValidation() {
    isPristine.value = true;
    if (!validateOn.value.lazy) {
      await validate(true);
    } else {
      internalErrorMessages.value = [];
    }
  }
  async function validate(silent = false) {
    const results = [];
    isValidating.value = true;
    for (const rule of props.rules) {
      if (results.length >= +(props.maxErrors ?? 1)) {
        break;
      }
      const handler = typeof rule === "function" ? rule : () => rule;
      const result = await handler(validationModel.value);
      if (result === true)
        continue;
      if (result !== false && typeof result !== "string") {
        console.warn(`${result} is not a valid value. Rule functions must return boolean true or a string.`);
        continue;
      }
      results.push(result || "");
    }
    internalErrorMessages.value = results;
    isValidating.value = false;
    isPristine.value = silent;
    return internalErrorMessages.value;
  }
  return {
    errorMessages,
    isDirty,
    isDisabled,
    isReadonly,
    isPristine,
    isValid,
    isValidating,
    reset,
    resetValidation,
    validate,
    validationClasses
  };
}
const makeVInputProps = propsFactory({
  id: String,
  appendIcon: IconValue,
  centerAffix: {
    type: Boolean,
    default: true
  },
  prependIcon: IconValue,
  hideDetails: [Boolean, String],
  hideSpinButtons: Boolean,
  hint: String,
  persistentHint: Boolean,
  messages: {
    type: [Array, String],
    default: () => []
  },
  direction: {
    type: String,
    default: "horizontal",
    validator: (v) => ["horizontal", "vertical"].includes(v)
  },
  "onClick:prepend": EventProp(),
  "onClick:append": EventProp(),
  ...makeComponentProps(),
  ...makeDensityProps(),
  ...only(makeDimensionProps(), [
    "maxWidth",
    "minWidth",
    "width"
  ]),
  ...makeThemeProps(),
  ...makeValidationProps()
}, "VInput");
const VInput = genericComponent()({
  name: "VInput",
  props: {
    ...makeVInputProps()
  },
  emits: {
    "update:modelValue": (value) => true
  },
  setup(props, { attrs, slots, emit }) {
    const { densityClasses } = useDensity(props);
    const { dimensionStyles } = useDimension(props);
    const { themeClasses } = provideTheme(props);
    const { rtlClasses } = useRtl();
    const { InputIcon } = useInputIcon(props);
    const uid = getUid();
    const id = computed(() => props.id || `input-${uid}`);
    const messagesId = computed(() => `${id.value}-messages`);
    const {
      errorMessages,
      isDirty,
      isDisabled,
      isReadonly,
      isPristine,
      isValid,
      isValidating,
      reset,
      resetValidation,
      validate,
      validationClasses
    } = useValidation(props, "v-input", id);
    const slotProps = computed(() => ({
      id,
      messagesId,
      isDirty,
      isDisabled,
      isReadonly,
      isPristine,
      isValid,
      isValidating,
      reset,
      resetValidation,
      validate
    }));
    const messages = computed(() => {
      var _a;
      if (((_a = props.errorMessages) == null ? void 0 : _a.length) || !isPristine.value && errorMessages.value.length) {
        return errorMessages.value;
      } else if (props.hint && (props.persistentHint || props.focused)) {
        return props.hint;
      } else {
        return props.messages;
      }
    });
    useRender(() => {
      var _a, _b, _c, _d;
      const hasPrepend = !!(slots.prepend || props.prependIcon);
      const hasAppend = !!(slots.append || props.appendIcon);
      const hasMessages = messages.value.length > 0;
      const hasDetails = !props.hideDetails || props.hideDetails === "auto" && (hasMessages || !!slots.details);
      return /* @__PURE__ */ React.createElement(
        "div",
        {
          class: [
            "v-input",
            `v-input--${props.direction}`,
            {
              "v-input--center-affix": props.centerAffix,
              "v-input--hide-spin-buttons": props.hideSpinButtons
            },
            densityClasses.value,
            themeClasses.value,
            rtlClasses.value,
            validationClasses.value,
            props.class
          ],
          style: [
            dimensionStyles.value,
            props.style
          ]
        },
        hasPrepend && /* @__PURE__ */ React.createElement("div", { key: "prepend", class: "v-input__prepend" }, (_a = slots.prepend) == null ? void 0 : _a.call(slots, slotProps.value), props.prependIcon && /* @__PURE__ */ React.createElement(
          InputIcon,
          {
            key: "prepend-icon",
            name: "prepend"
          }
        )),
        slots.default && /* @__PURE__ */ React.createElement("div", { class: "v-input__control" }, (_b = slots.default) == null ? void 0 : _b.call(slots, slotProps.value)),
        hasAppend && /* @__PURE__ */ React.createElement("div", { key: "append", class: "v-input__append" }, props.appendIcon && /* @__PURE__ */ React.createElement(
          InputIcon,
          {
            key: "append-icon",
            name: "append"
          }
        ), (_c = slots.append) == null ? void 0 : _c.call(slots, slotProps.value)),
        hasDetails && /* @__PURE__ */ React.createElement("div", { class: "v-input__details" }, /* @__PURE__ */ React.createElement(
          VMessages,
          {
            id: messagesId.value,
            active: hasMessages,
            messages: messages.value,
            "v-slots": { message: slots.message }
          }
        ), (_d = slots.details) == null ? void 0 : _d.call(slots, slotProps.value))
      );
    });
    return {
      reset,
      resetValidation,
      validate,
      isValid,
      errorMessages
    };
  }
});
const Refs = Symbol("Forwarded refs");
function getDescriptor(obj, key) {
  let currentObj = obj;
  while (currentObj) {
    const descriptor = Reflect.getOwnPropertyDescriptor(currentObj, key);
    if (descriptor)
      return descriptor;
    currentObj = Object.getPrototypeOf(currentObj);
  }
  return void 0;
}
function forwardRefs(target, ...refs) {
  target[Refs] = refs;
  return new Proxy(target, {
    get(target2, key) {
      if (Reflect.has(target2, key)) {
        return Reflect.get(target2, key);
      }
      if (typeof key === "symbol" || key.startsWith("$") || key.startsWith("__"))
        return;
      for (const ref2 of refs) {
        if (ref2.value && Reflect.has(ref2.value, key)) {
          const val = Reflect.get(ref2.value, key);
          return typeof val === "function" ? val.bind(ref2.value) : val;
        }
      }
    },
    has(target2, key) {
      if (Reflect.has(target2, key)) {
        return true;
      }
      if (typeof key === "symbol" || key.startsWith("$") || key.startsWith("__"))
        return false;
      for (const ref2 of refs) {
        if (ref2.value && Reflect.has(ref2.value, key)) {
          return true;
        }
      }
      return false;
    },
    set(target2, key, value) {
      if (Reflect.has(target2, key)) {
        return Reflect.set(target2, key, value);
      }
      if (typeof key === "symbol" || key.startsWith("$") || key.startsWith("__"))
        return false;
      for (const ref2 of refs) {
        if (ref2.value && Reflect.has(ref2.value, key)) {
          return Reflect.set(ref2.value, key, value);
        }
      }
      return false;
    },
    getOwnPropertyDescriptor(target2, key) {
      var _a;
      const descriptor = Reflect.getOwnPropertyDescriptor(target2, key);
      if (descriptor)
        return descriptor;
      if (typeof key === "symbol" || key.startsWith("$") || key.startsWith("__"))
        return;
      for (const ref2 of refs) {
        if (!ref2.value)
          continue;
        const descriptor2 = getDescriptor(ref2.value, key) ?? ("_" in ref2.value ? getDescriptor((_a = ref2.value._) == null ? void 0 : _a.setupState, key) : void 0);
        if (descriptor2)
          return descriptor2;
      }
      for (const ref2 of refs) {
        const childRefs = ref2.value && ref2.value[Refs];
        if (!childRefs)
          continue;
        const queue = childRefs.slice();
        while (queue.length) {
          const ref22 = queue.shift();
          const descriptor2 = getDescriptor(ref22.value, key);
          if (descriptor2)
            return descriptor2;
          const childRefs2 = ref22.value && ref22.value[Refs];
          if (childRefs2)
            queue.push(...childRefs2);
        }
      }
      return void 0;
    }
  });
}
const activeTypes = ["color", "file", "time", "date", "datetime-local", "week", "month"];
const makeVTextFieldProps = propsFactory({
  autofocus: Boolean,
  counter: [Boolean, Number, String],
  counterValue: [Number, Function],
  prefix: String,
  placeholder: String,
  persistentPlaceholder: Boolean,
  persistentCounter: Boolean,
  suffix: String,
  role: String,
  type: {
    type: String,
    default: "text"
  },
  modelModifiers: Object,
  ...makeVInputProps(),
  ...makeVFieldProps()
}, "VTextField");
const VTextField = genericComponent()({
  name: "VTextField",
  directives: { Intersect },
  inheritAttrs: false,
  props: makeVTextFieldProps(),
  emits: {
    "click:control": (e) => true,
    "mousedown:control": (e) => true,
    "update:focused": (focused) => true,
    "update:modelValue": (val) => true
  },
  setup(props, { attrs, emit, slots }) {
    const model = useProxiedModel(props, "modelValue");
    const { isFocused, focus, blur } = useFocus(props);
    const counterValue = computed(() => {
      return typeof props.counterValue === "function" ? props.counterValue(model.value) : typeof props.counterValue === "number" ? props.counterValue : (model.value ?? "").toString().length;
    });
    const max = computed(() => {
      if (attrs.maxlength)
        return attrs.maxlength;
      if (!props.counter || typeof props.counter !== "number" && typeof props.counter !== "string")
        return void 0;
      return props.counter;
    });
    const isPlainOrUnderlined = computed(() => ["plain", "underlined"].includes(props.variant));
    function onIntersect(isIntersecting, entries) {
      var _a, _b;
      if (!props.autofocus || !isIntersecting)
        return;
      (_b = (_a = entries[0].target) == null ? void 0 : _a.focus) == null ? void 0 : _b.call(_a);
    }
    const vInputRef = ref();
    const vFieldRef = ref();
    const inputRef = ref();
    const isActive = computed(() => activeTypes.includes(props.type) || props.persistentPlaceholder || isFocused.value || props.active);
    function onFocus() {
      var _a;
      if (inputRef.value !== document.activeElement) {
        (_a = inputRef.value) == null ? void 0 : _a.focus();
      }
      if (!isFocused.value)
        focus();
    }
    function onControlMousedown(e) {
      emit("mousedown:control", e);
      if (e.target === inputRef.value)
        return;
      onFocus();
      e.preventDefault();
    }
    function onControlClick(e) {
      onFocus();
      emit("click:control", e);
    }
    function onClear(e) {
      e.stopPropagation();
      onFocus();
      nextTick(() => {
        model.value = null;
        callEvent(props["onClick:clear"], e);
      });
    }
    function onInput(e) {
      var _a;
      const el = e.target;
      model.value = el.value;
      if (((_a = props.modelModifiers) == null ? void 0 : _a.trim) && ["text", "search", "password", "tel", "url"].includes(props.type)) {
        const caretPosition = [el.selectionStart, el.selectionEnd];
        nextTick(() => {
          el.selectionStart = caretPosition[0];
          el.selectionEnd = caretPosition[1];
        });
      }
    }
    useRender(() => {
      const hasCounter = !!(slots.counter || props.counter !== false && props.counter != null);
      const hasDetails = !!(hasCounter || slots.details);
      const [rootAttrs, inputAttrs] = filterInputAttrs(attrs);
      const { modelValue: _, ...inputProps } = VInput.filterProps(props);
      const fieldProps = filterFieldProps(props);
      return /* @__PURE__ */ React.createElement(
        VInput,
        {
          ref: vInputRef,
          "v-model": model.value,
          class: [
            "v-text-field",
            {
              "v-text-field--prefixed": props.prefix,
              "v-text-field--suffixed": props.suffix,
              "v-input--plain-underlined": isPlainOrUnderlined.value
            },
            props.class
          ],
          style: props.style,
          ...rootAttrs,
          ...inputProps,
          centerAffix: !isPlainOrUnderlined.value,
          focused: isFocused.value
        },
        {
          ...slots,
          default: ({
            id,
            isDisabled,
            isDirty,
            isReadonly,
            isValid
          }) => /* @__PURE__ */ React.createElement(
            VField,
            {
              ref: vFieldRef,
              onMousedown: onControlMousedown,
              onClick: onControlClick,
              "onClick:clear": onClear,
              "onClick:prependInner": props["onClick:prependInner"],
              "onClick:appendInner": props["onClick:appendInner"],
              role: props.role,
              ...fieldProps,
              id: id.value,
              active: isActive.value || isDirty.value,
              dirty: isDirty.value || props.dirty,
              disabled: isDisabled.value,
              focused: isFocused.value,
              error: isValid.value === false
            },
            {
              ...slots,
              default: ({
                props: { class: fieldClass, ...slotProps }
              }) => {
                const inputNode = /* @__PURE__ */ React.createElement(
                  "input",
                  {
                    ref: inputRef,
                    value: model.value,
                    onInput,
                    "v-intersect": [{
                      handler: onIntersect
                    }, null, ["once"]],
                    autofocus: props.autofocus,
                    readonly: isReadonly.value,
                    disabled: isDisabled.value,
                    name: props.name,
                    placeholder: props.placeholder,
                    size: 1,
                    type: props.type,
                    onFocus,
                    onBlur: blur,
                    ...slotProps,
                    ...inputAttrs
                  }
                );
                return /* @__PURE__ */ React.createElement(React.Fragment, null, props.prefix && /* @__PURE__ */ React.createElement("span", { class: "v-text-field__prefix" }, /* @__PURE__ */ React.createElement("span", { class: "v-text-field__prefix__text" }, props.prefix)), slots.default ? /* @__PURE__ */ React.createElement(
                  "div",
                  {
                    class: fieldClass,
                    "data-no-activator": ""
                  },
                  slots.default(),
                  inputNode
                ) : cloneVNode(inputNode, { class: fieldClass }), props.suffix && /* @__PURE__ */ React.createElement("span", { class: "v-text-field__suffix" }, /* @__PURE__ */ React.createElement("span", { class: "v-text-field__suffix__text" }, props.suffix)));
              }
            }
          ),
          details: hasDetails ? (slotProps) => {
            var _a;
            return /* @__PURE__ */ React.createElement(React.Fragment, null, (_a = slots.details) == null ? void 0 : _a.call(slots, slotProps), hasCounter && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", null), /* @__PURE__ */ React.createElement(
              VCounter,
              {
                active: props.persistentCounter || isFocused.value,
                value: counterValue.value,
                max: max.value,
                disabled: props.disabled,
                "v-slots:default": slots.counter
              }
            )));
          } : void 0
        }
      );
    });
    return forwardRefs({}, vInputRef, vFieldRef, inputRef);
  }
});
export {
  VLabel as V,
  VInput as a,
  useRtl as b,
  useLocale as c,
  flipAlign as d,
  flipCorner as e,
  flipSide as f,
  getAxis as g,
  forwardRefs as h,
  makeVTextFieldProps as i,
  useForm as j,
  VTextField as k,
  makeVInputProps as m,
  parseAnchor as p,
  useFocus as u
};
//# sourceMappingURL=_VTextField-74fe2c1a.mjs.map
